import { useState, type FormEvent } from 'react';
import { isValidCanvasName } from '@canvas/utils';
import { MOVIE_STUDIO_NAME } from '@/data/movieProduct';
import { MOTION_TEMPLATES } from '@/data/motionTemplates';

interface NewMovieModalProps {
  busy?: boolean;
  onClose: () => void;
  onCreate: (input: { name: string; templateId: string }) => Promise<void>;
}

export default function NewMovieModal({ busy = false, onClose, onCreate }: NewMovieModalProps) {
  const [templateId, setTemplateId] = useState(MOTION_TEMPLATES[0]?.id ?? '');
  const [name, setName] = useState(MOTION_TEMPLATES[0]?.label ?? 'Untitled movie');
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (!isValidCanvasName(trimmed)) {
      setError('Give the movie a name up to 255 characters.');
      return;
    }
    if (!templateId) {
      setError('Choose a format.');
      return;
    }
    await onCreate({ name: trimmed, templateId });
  };

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-movie-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="new-movie-title">New movie</h2>
        <p className="subtitle">
          {MOVIE_STUDIO_NAME} opens on a timed timeline. Pick the size you will export.
        </p>

        {error && <div className="error-banner">{error}</div>}

        <form className="modal-form" onSubmit={(e) => void submit(e)}>
          <div className="size-grid">
            {MOTION_TEMPLATES.map((template) => (
              <button
                key={template.id}
                type="button"
                className={`size-option ${templateId === template.id ? 'active' : ''}`}
                onClick={() => {
                  setTemplateId(template.id);
                  if (
                    !name.trim() ||
                    MOTION_TEMPLATES.some((item) => item.label === name)
                  ) {
                    setName(template.label);
                  }
                  setError(null);
                }}
              >
                <strong>{template.label}</strong>
                <span>
                  {template.width} × {template.height} · {template.hint}
                </span>
              </button>
            ))}
          </div>

          <div className="field">
            <label htmlFor="movie-name">Movie name</label>
            <input
              id="movie-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              disabled={busy}
            />
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? 'Creating…' : 'Create movie'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
